import { memo, useState } from "react";

import { useGetUserInfo } from "@synthesis/shared-services/auth/user/hooks";
import type { UserDto } from "@synthesis/shared-services/auth/user/types";
import { Button, Modal, Switch, type UserInformationUser } from "@synthesis/shared-ui";
import { observer } from "mobx-react-lite";

import { useNotificationAction } from "../hooks/use-notification-action";
import { useNotificationInfoItems } from "../hooks/use-notification-info-items";
import { type NotificationAction, type NotificationModalInfo, useNotificationsStore } from "../notifications-panel.lib";
import { useApplicationNotifications } from "../use-application-notifications";

const toUserInformationUser = (user: UserDto | null | undefined): UserInformationUser | null => {
  if (!user) {
    return null;
  }

  return {
    email: user.email ?? undefined,
    enabled: user.enabled ?? undefined,
    firstName: user.firstName ?? undefined,
    groups: user.groups?.map((group) => ({ id: group.id ?? undefined, name: group.name ?? undefined })) ?? undefined,
    id: user.id ?? undefined,
    lastName: user.lastName ?? undefined,
    middleName: user.middleName ?? undefined,
    position: user.position ?? undefined,
  };
};

type NotificationModalContentProps = {
  notificationInfo: NotificationModalInfo;
};

const NotificationModalContent = memo(({ notificationInfo }: NotificationModalContentProps) => {
  const store = useNotificationsStore()!;
  const { deleteNotification, markNotificationAsReadAsync } = useApplicationNotifications();
  const handleAction = useNotificationAction();

  const [isRead, setIsRead] = useState(!notificationInfo.isNew);

  const authorId = typeof notificationInfo.notification.params.authorId === "string" ? notificationInfo.notification.params.authorId : undefined;
  const authorQuery = useGetUserInfo({ path: { userId: authorId ?? "" } }, { query: { enabled: false } });
  const author = toUserInformationUser(authorQuery.data);

  const closeModal = () => store.closeModal();

  const handleAuthorClick = () => {
    if (authorId) {
      void authorQuery.refetch();
    }
  };

  const infoItems = useNotificationInfoItems({
    author,
    authorName: notificationInfo.author,
    body: notificationInfo.body,
    date: notificationInfo.date,
    handleAuthorClick,
  });

  const handleMarkAsRead = (checked: boolean) => {
    if (!checked || isRead) {
      return;
    }

    void markNotificationAsReadAsync(notificationInfo.notificationId).then(() => {
      setIsRead(true);
    });
  };

  const handleDeleteNotification = () => {
    deleteNotification(notificationInfo.notificationId);
    closeModal();
  };

  const handleNotificationAction = (action: NotificationAction) => {
    handleAction(action);
    closeModal();
  };

  return (
    <Modal.Content className="w-[60vw] min-w-[400px]">
      <Modal.Title>Уведомление</Modal.Title>
      <div className="bg-neutrals-99 p-8">
        <div className="bg-neutrals-white rounded-2xl p-8">
          <h2 className="heading-h4 text-type-common">{notificationInfo.title}</h2>
          <div className="mt-4 flex items-center gap-3">
            <Switch checked={isRead} onCheckedChange={handleMarkAsRead} />
            <p className="paragraph-sm text-type-common">Прочитано</p>
          </div>

          <div className="mt-6">
            {infoItems.map((item) => (
              <div className="border-neutrals-90 flex items-start border-b py-3 last:border-b-0" key={item.title}>
                <p className="paragraph-base text-type-minor mt-0.5 w-56 min-w-56">{item.title}</p>
                <div className="flex w-full min-w-0 items-center gap-2">
                  {item.icon && <item.icon className="text-neutrals-60 mt-0.5 size-5 min-w-5" />}
                  <div className="subtitle-sm text-type-minor w-full">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-4">
          {notificationInfo.isShowButton &&
            notificationInfo.actions.map((action) => (
              <Button
                type="button"
                size="lg"
                key={`${action.title}-${JSON.stringify(action.payload)}`}
                onClick={() => handleNotificationAction(action)}
              >
                {action.title}
              </Button>
            ))}
          <Button type="button" theme="primary" size="lg" className="bg-system-error-50" onClick={handleDeleteNotification}>
            Удалить
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
});

NotificationModalContent.displayName = "NotificationModalContent";

export const NotificationModal = observer(() => {
  const store = useNotificationsStore()!;

  return (
    <Modal.Root isOpen={Boolean(store.modalInfo)} setIsOpen={(isOpen) => !isOpen && store.closeModal()}>
      {store.modalInfo && <NotificationModalContent key={store.modalInfo.notificationId} notificationInfo={store.modalInfo} />}
    </Modal.Root>
  );
});

NotificationModal.displayName = "NotificationModal";
